import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import FeatureTip from '../../components/FeatureTip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  BookOpen, TrendingUp, TrendingDown, DollarSign, Plus, Trash2, Save,
  Upload, Lock, Sparkles, FileText, Settings, ChevronDown, ChevronUp,
  RefreshCw, Calendar, Loader2
} from 'lucide-react';

const TABS = [
  { id: 'overview', label: 'Översikt' },
  { id: 'journal', label: 'Huvudbok' },
  { id: 'receipts', label: 'Kvitton' },
  { id: 'income', label: 'Resultaträkning' },
  { id: 'balance', label: 'Balansräkning' },
  { id: 'periods', label: 'Periodstängning' },
  { id: 'settings', label: 'Inställningar' },
];

export default function AccountingPage() {
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [journal, setJournal] = useState({ entries: [], total: 0 });
  const [income, setIncome] = useState(null);
  const [balance, setBalance] = useState(null);
  const [settings, setSettings] = useState(null);
  const [periodCloses, setPeriodCloses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [closing, setClosing] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({ date: new Date().toISOString().slice(0, 10), description: '', lines: [{ account: '', account_name: '', debit: '', credit: '' }] });
  // Fas 3 (#6): filter per fakturabolag
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [billingProfileFilter, setBillingProfileFilter] = useState('');
  const fileRef = useRef(null);
  const receiptFileRef = useRef(null);
  // Kvitton / receipts state
  const [receipts, setReceipts] = useState([]);
  const [receiptMode, setReceiptMode] = useState('draft'); // 'draft' | 'auto_book'
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptStatusFilter, setReceiptStatusFilter] = useState('');
  const [editingReceipt, setEditingReceipt] = useState(null); // { id, ...extracted } for review-modal
  const [bulkProgress, setBulkProgress] = useState([]); // [{name, status, error}]
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    loadData();
    api.listBillingProfiles().then(r => setBillingProfiles(r.data || [])).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sumRes, setRes] = await Promise.all([
        api.getAccountingSummary(),
        api.getAccountingSettings(),
      ]);
      setSummary(sumRes.data);
      setSettings(setRes.data);
      if (!periodStart && sumRes.data?.fiscal_year_start) {
        setPeriodStart(sumRes.data.fiscal_year_start);
        setPeriodEnd(new Date().toISOString().slice(0, 10));
      }
    } catch (err) { console.warn('Load accounting failed:', err?.message); }
    finally { setLoading(false); }
  };

  const loadJournal = async () => {
    try {
      const res = await api.getJournalEntries({
        period_start: periodStart, period_end: periodEnd,
        search: searchQuery, limit: 200,
        billing_profile_id: billingProfileFilter || undefined,
      });
      setJournal(res.data);
    } catch (err) { console.warn('Load journal failed:', err?.message); }
  };

  const loadReports = async () => {
    try {
      const [incRes, balRes] = await Promise.all([
        api.getIncomeStatement({ period_start: periodStart, period_end: periodEnd, billing_profile_id: billingProfileFilter || undefined }),
        api.getBalanceSheet({ as_of_date: periodEnd, billing_profile_id: billingProfileFilter || undefined }),
      ]);
      setIncome(incRes.data);
      setBalance(balRes.data);
    } catch (err) { console.warn('Load reports failed:', err?.message); }
  };

  const loadPeriodCloses = async () => {
    try {
      const res = await api.getPeriodCloses();
      setPeriodCloses(res.data);
    } catch (err) { console.warn('Load period closes failed:', err?.message); }
  };

  useEffect(() => {
    if (tab === 'journal') loadJournal();
    if (tab === 'income' || tab === 'balance') loadReports();
    if (tab === 'periods') loadPeriodCloses();
    if (tab === 'receipts') loadReceipts();
  }, [tab, receiptStatusFilter]);

  const loadReceipts = async () => {
    try {
      const res = await api.listReceipts(receiptStatusFilter || undefined);
      setReceipts(res.data || []);
    } catch (err) { console.warn('Load receipts failed:', err?.message); }
  };

  const handleReceiptUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    await processReceiptFiles(files);
    if (receiptFileRef.current) receiptFileRef.current.value = '';
  };

  const processReceiptFiles = async (files) => {
    setReceiptUploading(true);
    const init = files.map(f => ({ name: f.name, status: 'pending', error: null, extracted: null, file: f }));
    setBulkProgress(init);
    let bookedCount = 0;
    let draftCount = 0;
    let dupCount = 0;
    let failedCount = 0;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBulkProgress(prev => prev.map((p, idx) => idx === i ? { ...p, status: 'uploading' } : p));
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'receipt');
      fd.append('mode', receiptMode);
      try {
        const res = await api.uploadReceipt(fd);
        const doc = res.data;
        // Semantisk duplikat-varning från backend
        if (doc.duplicate_warning) {
          dupCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'duplicate_warning', supplier: doc.extracted?.supplier_name,
                amount: doc.extracted?.total_amount, dup: doc.duplicate_warning, receiptId: doc.id }
            : p));
        } else if (doc.status === 'booked') {
          bookedCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'booked', supplier: doc.extracted?.supplier_name, amount: doc.extracted?.total_amount }
            : p));
        } else if (doc.extracted) {
          draftCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'draft', supplier: doc.extracted?.supplier_name, amount: doc.extracted?.total_amount }
            : p));
        } else {
          failedCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'manual' }
            : p));
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        // 409 = exakt fil-duplikat
        if (err.response?.status === 409 && detail?.error === 'duplicate_file') {
          dupCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'duplicate_file', dup: detail,
                supplier: detail.existing_supplier, amount: detail.existing_total }
            : p));
        } else {
          failedCount++;
          setBulkProgress(prev => prev.map((p, idx) => idx === i
            ? { ...p, status: 'error', error: (typeof detail === 'string' ? detail : detail?.message) || err.message }
            : p));
        }
      }
    }
    const parts = [];
    if (bookedCount) parts.push(`${bookedCount} auto-bokförda`);
    if (draftCount) parts.push(`${draftCount} utkast`);
    if (dupCount) parts.push(`${dupCount} duplikat blockerade`);
    if (failedCount) parts.push(`${failedCount} misslyckades`);
    if (parts.length) toast.success(`Klart: ${parts.join(', ')}`);
    loadReceipts();
    loadData();
    setReceiptUploading(false);
  };

  const forceUploadDuplicate = async (idx) => {
    const item = bulkProgress[idx];
    if (!item?.file) return;
    setBulkProgress(prev => prev.map((p, i) => i === idx ? { ...p, status: 'uploading' } : p));
    const fd = new FormData();
    fd.append('file', item.file);
    fd.append('doc_type', 'receipt');
    fd.append('mode', receiptMode);
    fd.append('force', 'true');
    try {
      const res = await api.uploadReceipt(fd);
      const doc = res.data;
      setBulkProgress(prev => prev.map((p, i) => i === idx
        ? { ...p, status: doc.status === 'booked' ? 'booked' : 'draft',
            supplier: doc.extracted?.supplier_name, amount: doc.extracted?.total_amount }
        : p));
      toast.success('Uppladdad trots duplikat-varning');
      loadReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || 'Kunde inte ladda upp');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['pdf', 'png', 'jpg', 'jpeg', 'webp'].includes(ext);
    });
    if (!files.length) {
      toast.error('Inga giltiga filer (PDF/PNG/JPG/WebP)');
      return;
    }
    await processReceiptFiles(files);
  };

  const handleBookReceipt = async () => {
    if (!editingReceipt) return;
    const { id, ...payload } = editingReceipt;
    try {
      await api.bookReceipt(id, payload);
      toast.success('Kvitto bokfört');
      setEditingReceipt(null);
      loadReceipts();
      if (tab === 'journal') loadJournal();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte bokföra');
    }
  };

  const handleDeleteReceipt = async (id) => {
    if (!window.confirm('Ta bort kvittot?')) return;
    try {
      await api.deleteReceipt(id);
      toast.success('Kvitto borttaget');
      loadReceipts();
    } catch (err) { toast.error('Kunde inte ta bort'); }
  };

  const openReceiptFile = async (id) => {
    try {
      const res = await api.getReceiptFile(id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (err) { toast.error('Kunde inte öppna filen'); }
  };

  const handleAutoBook = async () => {
    setBooking(true);
    try {
      const res = await api.autoBookAll();
      toast.success(res.data.message);
      loadData();
      if (tab === 'journal') loadJournal();
    } catch (err) { toast.error('Auto-bokföring misslyckades'); }
    finally { setBooking(false); }
  };

  const handleClosePeriod = async () => {
    if (!periodStart || !periodEnd) { toast.error('Ange period'); return; }
    setClosing(true);
    try {
      const res = await api.closePeriod({ period_start: periodStart, period_end: periodEnd });
      toast.success('Period stängd!');
      setPeriodCloses(prev => [res.data, ...prev]);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunde inte stänga period'); }
    finally { setClosing(false); }
  };

  const handleSaveEntry = async () => {
    try {
      await api.createJournalEntry(newEntry);
      toast.success('Verifikation sparad');
      setShowNewEntry(false);
      setNewEntry({ date: new Date().toISOString().slice(0, 10), description: '', lines: [{ account: '', account_name: '', debit: '', credit: '' }] });
      loadJournal();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunde inte spara'); }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Ta bort verifikation?')) return;
    try {
      await api.deleteJournalEntry(id);
      toast.success('Borttagen');
      loadJournal();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kunde inte ta bort'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source', 'fortnox');
    try {
      const res = await api.importAccountingHistory(fd);
      toast.success(res.data.message);
      loadJournal();
      loadData();
    } catch (err) { toast.error('Import misslyckades'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSaveSettings = async () => {
    try {
      const res = await api.updateAccountingSettings(settings);
      setSettings(res.data);
      toast.success('Inställningar sparade');
      loadData();
    } catch (err) { toast.error('Kunde inte spara'); }
  };

  const addLine = () => setNewEntry(e => ({ ...e, lines: [...e.lines, { account: '', account_name: '', debit: '', credit: '' }] }));
  const removeLine = (i) => setNewEntry(e => ({ ...e, lines: e.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) => setNewEntry(e => ({ ...e, lines: e.lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l) }));

  const fmtKr = (v) => (v || 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (loading) return <div className="flex items-center justify-center h-48"><div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <FeatureTip tipId="accounting" title="AI Auto-bokföring" body="Klicka 'Bokför alla' så läser AI:n dina fakturor och tidrapporter och skapar verifikat enligt BAS-kontoplanen. Du verifierar med ett klick." />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bokföring</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-driven bokföring med BAS-kontoplan</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleAutoBook} disabled={booking} data-testid="auto-book-btn">
            {booking ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
            {booking ? 'Bokför...' : 'AI Auto-bokför'}
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.json,.si,.se,.sie,.se4,.xlsx,.xls,.txt,text/*" onChange={handleImport} className="hidden" data-testid="import-accounting-file" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-accounting-btn">
            <Upload size={14} className="mr-1" /> Importera
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${tab === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`acc-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && summary && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={TrendingUp} color="green" label="Intäkter YTD" value={`${fmtKr(summary.ytd_revenue)} kr`} />
            <StatCard icon={TrendingDown} color="red" label="Kostnader YTD" value={`${fmtKr(summary.ytd_expenses)} kr`} />
            <StatCard icon={DollarSign} color={summary.ytd_profit >= 0 ? 'green' : 'red'} label="Resultat YTD" value={`${fmtKr(summary.ytd_profit)} kr`} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="text-xs font-bold text-muted-foreground uppercase">Räkenskapsår</p>
              <p className="text-lg font-bold mt-1">{summary.fiscal_year_start}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-5">
              <p className="text-xs font-bold text-muted-foreground uppercase">Verifikationer</p>
              <p className="text-lg font-bold mt-1">{summary.fy_entries} <span className="text-sm text-muted-foreground font-normal">(totalt: {summary.total_entries})</span></p>
            </div>
          </div>
        </div>
      )}

      {/* JOURNAL */}
      {tab === 'journal' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Sök verifikationer..." className="max-w-xs" data-testid="journal-search" />
            {billingProfiles.length > 0 && (
              <select
                value={billingProfileFilter}
                onChange={e => setBillingProfileFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                data-testid="accounting-bp-filter-journal"
              >
                <option value="">Alla fakturabolag</option>
                {billingProfiles.map(bp => (
                  <option key={bp.id} value={bp.id}>{bp.label || bp.company_name}</option>
                ))}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={loadJournal}><RefreshCw size={14} /></Button>
            <Button size="sm" onClick={() => setShowNewEntry(!showNewEntry)} data-testid="new-entry-btn"><Plus size={14} className="mr-1" /> Ny verifikation</Button>
          </div>

          {showNewEntry && (
            <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="new-entry-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Datum</Label><Input type="date" value={newEntry.date} onChange={e => setNewEntry(ne => ({ ...ne, date: e.target.value }))} className="mt-1" /></div>
                <div><Label className="text-xs">Beskrivning</Label><Input value={newEntry.description} onChange={e => setNewEntry(ne => ({ ...ne, description: e.target.value }))} className="mt-1" placeholder="T.ex. Leverantörsfaktura" /></div>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-muted-foreground"><th className="text-left py-1">Konto</th><th className="text-left py-1">Namn</th><th className="text-right py-1">Debet</th><th className="text-right py-1">Kredit</th><th></th></tr></thead>
                <tbody>
                  {newEntry.lines.map((l, i) => (
                    <tr key={i}>
                      <td className="py-1 pr-2"><Input className="h-8 text-sm w-20" value={l.account} onChange={e => updateLine(i, 'account', e.target.value)} placeholder="1510" /></td>
                      <td className="py-1 pr-2"><Input className="h-8 text-sm" value={l.account_name} onChange={e => updateLine(i, 'account_name', e.target.value)} placeholder="Kundfordringar" /></td>
                      <td className="py-1 pr-2"><Input className="h-8 text-sm text-right w-24" type="number" value={l.debit} onChange={e => updateLine(i, 'debit', e.target.value)} /></td>
                      <td className="py-1 pr-2"><Input className="h-8 text-sm text-right w-24" type="number" value={l.credit} onChange={e => updateLine(i, 'credit', e.target.value)} /></td>
                      <td><button onClick={() => removeLine(i)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between">
                <Button size="sm" variant="outline" onClick={addLine}><Plus size={14} className="mr-1" /> Rad</Button>
                <Button size="sm" onClick={handleSaveEntry} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="save-entry-btn"><Save size={14} className="mr-1" /> Spara</Button>
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Datum</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Beskrivning</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Konton</th>
                  <th className="px-4 py-2.5 text-right text-xs font-bold text-muted-foreground uppercase">Belopp</th>
                  <th className="px-4 py-2.5 text-center text-xs font-bold text-muted-foreground uppercase">Källa</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {journal.entries.map(e => {
                  const totalDebit = e.lines?.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0) || 0;
                  return (
                    <tr key={e.id} className="hover:bg-muted/30" data-testid={`journal-row-${e.id}`}>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{e.date}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{e.description}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{e.lines?.map(l => l.account).join(', ')}</td>
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{fmtKr(totalDebit)} kr</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${e.source === 'manual' ? 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300' : e.source === 'invoice' ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' : 'bg-muted text-muted-foreground'}`}>
                          {e.source === 'manual' ? 'Manuell' : e.source === 'invoice' ? 'Faktura' : e.source === 'payroll' ? 'Lön' : e.source === 'purchase' ? 'Inköp' : e.source}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {e.is_locked ? <Lock size={14} className="text-muted-foreground" /> : (
                          <button onClick={() => handleDeleteEntry(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {journal.entries.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Inga verifikationer ännu. Klicka &quot;AI Auto-bokför&quot; för att börja.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* RECEIPTS */}
      {tab === 'receipts' && (
        <div className="space-y-4" data-testid="receipts-tab">
          <div className="bg-card border border-border rounded-xl p-5 space-y-4">
            <div>
              <h3 className="text-base font-bold flex items-center gap-2">
                <Sparkles size={16} className="text-[#E8613A]" />
                Ladda upp kvitton för AI-bokföring
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Gemini 3 Pro läser varje PDF/bild och föreslår BAS-konto, moms och belopp.
                Dra in flera filer samtidigt — perfekt för att bokföra hela månadens utgifter på en gång.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="receipt-mode"
                    value="draft"
                    checked={receiptMode === 'draft'}
                    onChange={() => setReceiptMode('draft')}
                    data-testid="receipt-mode-draft"
                  />
                  <span>Granska först</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="receipt-mode"
                    value="auto_book"
                    checked={receiptMode === 'auto_book'}
                    onChange={() => setReceiptMode('auto_book')}
                    data-testid="receipt-mode-auto"
                  />
                  <span>Auto-bokför direkt</span>
                </label>
              </div>
              <select
                value={receiptStatusFilter}
                onChange={e => setReceiptStatusFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                data-testid="receipt-status-filter"
              >
                <option value="">Alla status</option>
                <option value="draft">Utkast</option>
                <option value="booked">Bokförd</option>
                <option value="needs_manual_entry">Kräver manuell</option>
              </select>
              <Button size="sm" variant="outline" onClick={loadReceipts}><RefreshCw size={14} /></Button>
            </div>

            {/* Dropzone */}
            <input
              ref={receiptFileRef}
              type="file"
              multiple
              accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
              onChange={handleReceiptUpload}
              className="hidden"
              data-testid="receipt-file-input"
            />
            <div
              onClick={() => !receiptUploading && receiptFileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                isDragging ? 'border-[#E8613A] bg-[#E8613A]/5' : 'border-border hover:border-[#E8613A]/50 hover:bg-muted/30'
              } ${receiptUploading ? 'pointer-events-none opacity-60' : ''}`}
              data-testid="receipt-dropzone"
            >
              {receiptUploading ? (
                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 size={28} className="animate-spin text-[#E8613A]" />
                  <span>AI tolkar kvitton...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={28} className="text-[#E8613A]" />
                  <p className="text-sm font-medium">Dra & släpp flera kvitton här</p>
                  <p className="text-xs text-muted-foreground">
                    eller klicka för att välja filer &nbsp;·&nbsp; PDF, PNG, JPG, WebP &nbsp;·&nbsp; max 15 MB/fil
                  </p>
                </div>
              )}
            </div>

            {/* Bulk progress list */}
            {bulkProgress.length > 0 && (
              <div className="border border-border rounded-lg divide-y divide-border max-h-72 overflow-auto" data-testid="bulk-progress">
                {bulkProgress.map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{p.name}</span>
                      {p.supplier && <span className="text-xs text-muted-foreground truncate">— {p.supplier}</span>}
                      {p.amount && <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtKr(p.amount)} kr</span>}
                    </div>
                    <div className="flex items-center gap-2 text-xs whitespace-nowrap">
                      {p.status === 'pending' && <span className="text-muted-foreground">Väntar</span>}
                      {p.status === 'uploading' && <><Loader2 size={12} className="animate-spin" /> <span>Tolkar...</span></>}
                      {p.status === 'booked' && <span className="text-green-600 dark:text-green-400 font-medium">✓ Bokförd</span>}
                      {p.status === 'draft' && <span className="text-amber-600 dark:text-amber-400 font-medium">✓ Utkast</span>}
                      {p.status === 'manual' && <span className="text-muted-foreground">Manuell</span>}
                      {p.status === 'duplicate_file' && (
                        <>
                          <span className="text-orange-600 dark:text-orange-400 font-medium" title="Exakt samma fil finns redan">
                            ⚠ Duplikat fil
                          </span>
                          <Button size="sm" variant="outline" onClick={() => forceUploadDuplicate(i)} className="h-6 px-2 text-xs" data-testid={`force-upload-${i}`}>
                            Ladda ändå
                          </Button>
                        </>
                      )}
                      {p.status === 'duplicate_warning' && (
                        <span className="text-orange-600 dark:text-orange-400 font-medium" title="Samma leverantör + datum + belopp">
                          ⚠ Liknande finns
                        </span>
                      )}
                      {p.status === 'error' && <span className="text-red-600 dark:text-red-400" title={p.error}>Fel</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Receipts list */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Datum</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Leverantör</th>
                  <th className="px-4 py-2.5 text-left text-xs font-bold text-muted-foreground uppercase">Konto</th>
                  <th className="px-4 py-2.5 text-right text-xs font-bold text-muted-foreground uppercase">Belopp</th>
                  <th className="px-4 py-2.5 text-center text-xs font-bold text-muted-foreground uppercase">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-bold text-muted-foreground uppercase">Åtgärder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {receipts.map(r => {
                  const ex = r.extracted || {};
                  const statusBadge = r.status === 'booked'
                    ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300'
                    : r.status === 'draft'
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                      : 'bg-muted text-muted-foreground';
                  const statusLbl = r.status === 'booked' ? 'Bokförd' : r.status === 'draft' ? 'Utkast' : 'Manuell';
                  return (
                    <tr key={r.id} className="hover:bg-muted/30" data-testid={`receipt-row-${r.id}`}>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{ex.date || r.uploaded_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 font-medium text-foreground">
                        <button onClick={() => openReceiptFile(r.id)} className="hover:underline text-left flex items-center gap-1.5" data-testid={`open-receipt-${r.id}`}>
                          <FileText size={14} className="text-muted-foreground" />
                          {ex.supplier_name || r.filename}
                        </button>
                        {r.duplicate_warning && (
                          <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5" title={r.duplicate_warning.message}>
                            ⚠ Möjligt duplikat
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {ex.suggested_account ? `${ex.suggested_account} ${ex.suggested_account_name || ''}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium whitespace-nowrap">
                        {ex.total_amount ? `${fmtKr(ex.total_amount)} kr` : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>{statusLbl}</span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {r.status !== 'booked' && (
                          <Button size="sm" variant="outline" onClick={() => setEditingReceipt({ id: r.id, ...(r.extracted || {}) })} className="mr-1" data-testid={`review-receipt-${r.id}`}>
                            Granska
                          </Button>
                        )}
                        <button onClick={() => handleDeleteReceipt(r.id)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-receipt-${r.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {receipts.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Inga kvitton uppladdade ännu.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Receipt review modal */}
      {editingReceipt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="receipt-review-modal">
          <div className="bg-card border border-border rounded-xl p-6 max-w-lg w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold">Granska kvitto</h3>
              <button onClick={() => setEditingReceipt(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Leverantör</Label>
                <Input value={editingReceipt.supplier_name || ''} onChange={e => setEditingReceipt(r => ({ ...r, supplier_name: e.target.value }))} className="mt-1" data-testid="review-supplier" />
              </div>
              <div>
                <Label className="text-xs">Datum</Label>
                <Input type="date" value={editingReceipt.date || ''} onChange={e => setEditingReceipt(r => ({ ...r, date: e.target.value }))} className="mt-1" data-testid="review-date" />
              </div>
              <div>
                <Label className="text-xs">Belopp inkl. moms</Label>
                <Input type="number" step="0.01" value={editingReceipt.total_amount || ''} onChange={e => setEditingReceipt(r => ({ ...r, total_amount: parseFloat(e.target.value) }))} className="mt-1" data-testid="review-total" />
              </div>
              <div>
                <Label className="text-xs">Moms</Label>
                <Input type="number" step="0.01" value={editingReceipt.vat_amount || ''} onChange={e => setEditingReceipt(r => ({ ...r, vat_amount: parseFloat(e.target.value) }))} className="mt-1" data-testid="review-vat" />
              </div>
              <div>
                <Label className="text-xs">BAS-konto</Label>
                <Input value={editingReceipt.suggested_account || editingReceipt.account || ''} onChange={e => setEditingReceipt(r => ({ ...r, suggested_account: e.target.value }))} className="mt-1" data-testid="review-account" />
              </div>
              <div>
                <Label className="text-xs">Kontonamn</Label>
                <Input value={editingReceipt.suggested_account_name || ''} onChange={e => setEditingReceipt(r => ({ ...r, suggested_account_name: e.target.value }))} className="mt-1" data-testid="review-account-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Beskrivning</Label>
                <Input value={editingReceipt.description || ''} onChange={e => setEditingReceipt(r => ({ ...r, description: e.target.value }))} className="mt-1" data-testid="review-desc" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingReceipt(null)}>Avbryt</Button>
              <Button onClick={handleBookReceipt} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="confirm-book-receipt">
                <Save size={14} className="mr-1" /> Bokför
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* INCOME STATEMENT */}
      {tab === 'income' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-40" />
            <span className="text-muted-foreground">—</span>
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-40" />
            {billingProfiles.length > 0 && (
              <select
                value={billingProfileFilter}
                onChange={e => setBillingProfileFilter(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                data-testid="accounting-bp-filter-income"
              >
                <option value="">Alla fakturabolag</option>
                {billingProfiles.map(bp => (
                  <option key={bp.id} value={bp.id}>{bp.label || bp.company_name}</option>
                ))}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={loadReports}><RefreshCw size={14} className="mr-1" /> Uppdatera</Button>
          </div>
          {income && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h3 className="text-base font-bold">Resultaträkning {income.period_start} — {income.period_end}</h3>
              <div>
                <p className="text-xs font-bold text-green-600 dark:text-green-400 uppercase mb-2">Intäkter</p>
                {income.revenue.map(r => (
                  <div key={r.account} className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{r.account} {r.name}</span><span className="font-medium">{fmtKr(r.amount)} kr</span></div>
                ))}
                <div className="flex justify-between py-2 text-sm font-bold border-t border-border mt-2"><span>Summa intäkter</span><span className="text-green-600">{fmtKr(income.total_revenue)} kr</span></div>
              </div>
              <div>
                <p className="text-xs font-bold text-red-600 dark:text-red-400 uppercase mb-2">Kostnader</p>
                {income.expenses.map(e => (
                  <div key={e.account} className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{e.account} {e.name}</span><span className="font-medium">{fmtKr(e.amount)} kr</span></div>
                ))}
                <div className="flex justify-between py-2 text-sm font-bold border-t border-border mt-2"><span>Summa kostnader</span><span className="text-red-600">{fmtKr(income.total_expenses)} kr</span></div>
              </div>
              <div className={`flex justify-between py-3 text-lg font-bold border-t-2 border-border ${income.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <span>Resultat</span><span>{fmtKr(income.net_income)} kr</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* BALANCE SHEET */}
      {tab === 'balance' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-40" />
            <Button size="sm" variant="outline" onClick={loadReports}><RefreshCw size={14} className="mr-1" /> Uppdatera</Button>
          </div>
          {balance && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h3 className="text-base font-bold">Balansräkning per {balance.as_of_date}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase mb-2">Tillgångar</p>
                  {balance.assets.map(a => (
                    <div key={a.account} className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{a.account} {a.name}</span><span className="font-medium">{fmtKr(a.balance)} kr</span></div>
                  ))}
                  <div className="flex justify-between py-2 text-sm font-bold border-t border-border mt-2"><span>Summa tillgångar</span><span>{fmtKr(balance.total_assets)} kr</span></div>
                </div>
                <div>
                  <p className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase mb-2">Skulder</p>
                  {balance.liabilities.map(l => (
                    <div key={l.account} className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{l.account} {l.name}</span><span className="font-medium">{fmtKr(l.balance)} kr</span></div>
                  ))}
                  <p className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase mb-2 mt-4">Eget kapital</p>
                  {balance.equity.map(e => (
                    <div key={e.account} className="flex justify-between py-1 text-sm"><span className="text-muted-foreground">{e.account} {e.name}</span><span className="font-medium">{fmtKr(e.balance)} kr</span></div>
                  ))}
                  <div className="flex justify-between py-2 text-sm font-bold border-t border-border mt-2"><span>Summa EK + skulder</span><span>{fmtKr(balance.total_liabilities + balance.total_equity)} kr</span></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PERIOD CLOSES */}
      {tab === 'periods' && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-base font-bold mb-3">Stäng period</h3>
            <p className="text-sm text-muted-foreground mb-4">AI bokför alla händelser, genererar sammanfattning och låser verifikationer.</p>
            <div className="flex items-center gap-3">
              <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-40" />
              <span className="text-muted-foreground">—</span>
              <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-40" />
              <Button onClick={handleClosePeriod} disabled={closing} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="close-period-btn">
                {closing ? <><Loader2 size={14} className="mr-1 animate-spin" /> Stänger...</> : <><Lock size={14} className="mr-1" /> Stäng period</>}
              </Button>
            </div>
          </div>
          {periodCloses.map(pc => (
            <div key={pc.id} className="bg-card border border-border rounded-xl p-5" data-testid={`period-close-${pc.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-bold">{pc.period_start} — {pc.period_end}</p>
                  <p className="text-xs text-muted-foreground">Stängd {new Date(pc.closed_at).toLocaleString('sv-SE')}</p>
                </div>
                <span className="text-sm font-bold">{fmtKr(pc.income_statement?.net_income)} kr</span>
              </div>
              {pc.ai_summary && (
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-foreground whitespace-pre-line mt-2">{pc.ai_summary}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* SETTINGS */}
      {tab === 'settings' && settings && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h3 className="text-base font-bold">Bokföringsinställningar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase">Räkenskapsårets startmånad</Label>
              <select value={settings.fiscal_year_start_month} onChange={e => setSettings(s => ({ ...s, fiscal_year_start_month: parseInt(e.target.value) }))}
                className="w-full h-10 px-3 border border-input rounded-lg text-sm bg-background text-foreground mt-1">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['Jan','Feb','Mar','Apr','Maj','Jun','Jul','Aug','Sep','Okt','Nov','Dec'][m-1]}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Startdag</Label>
              <Input type="number" min={1} max={28} value={settings.fiscal_year_start_day} onChange={e => setSettings(s => ({ ...s, fiscal_year_start_day: parseInt(e.target.value) }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Momssats (%)</Label>
              <Input type="number" value={settings.vat_rate} onChange={e => setSettings(s => ({ ...s, vat_rate: parseFloat(e.target.value) }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Valuta</Label>
              <Input value={settings.currency} onChange={e => setSettings(s => ({ ...s, currency: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="space-y-2 pt-2">
            {[['auto_book_invoices', 'Auto-bokför fakturor'], ['auto_book_payroll', 'Auto-bokför löner'], ['auto_book_purchases', 'Auto-bokför inköp']].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={settings[key] || false} onChange={e => setSettings(s => ({ ...s, [key]: e.target.checked }))} className="rounded" />
                {label}
              </label>
            ))}
          </div>
          <Button onClick={handleSaveSettings} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="save-accounting-settings-btn">
            <Save size={14} className="mr-1" /> Spara inställningar
          </Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  const colorMap = {
    green: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
    red: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400',
    blue: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400',
  };
  return (
    <div className={`border rounded-xl p-5 ${colorMap[color] || colorMap.blue}`}>
      <div className="flex items-center gap-2 mb-2"><Icon size={16} /><span className="text-xs font-bold uppercase">{label}</span></div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
